// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, OnDestroy, OnChanges, OnInit } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { Observable, Subject, observable, of } from 'rxjs';
import {
  INPUT_TEXT_REQUIRED_ERROR,
  NAME_PATTERN_ERROR,
  DESCRIPTION_PATTERN_ERROR,
  NAME_PATTERN,
  DESCRIPTION_PATTERN
} from '@app/core/services/input-text-validator.service';
import { ActivatedRoute } from '@angular/router';
import {
  RolesService,
  readParameterFromRouterState,
  GoAction,
  ScopeCategory,
  RoleObject,
  PlatformService,
  ServiceRole
} from '@app/core';
import { AlertService } from '@app/core/services/alert.service';
import { Store, State } from '@ngrx/store';
import { RoleDetailsFacadeService } from '@app/access-management/state/role-details.facade';
import { takeUntil, take } from 'rxjs/operators';
import { LoadingRolesAction, LoadingRoleDetailsAction } from '@app/access-management/state';
import { SelectedRows } from '@app/shared/data-table-ajax/data-table-ajax.component';

@Component({
  selector: 'app-role-edit',
  templateUrl: './role-edit.component.html',
  styleUrls: ['./role-edit.component.scss']
})
export class RoleEditComponent implements OnDestroy {
  _roleId = '';
  roleForm: FormGroup = new FormGroup({});
  role$: Observable<any>;
  roleNameControl: FormControl = new FormControl();
  roleTitleControl: FormControl = new FormControl();
  roleDescriptionControl: FormControl = new FormControl();
  destroy$: Subject<void> = new Subject();
  selectedObjects: ScopeCategory[] | undefined = undefined;
  selectedServices: PlatformService[] | undefined = undefined;
  objects$: Observable<ScopeCategory[]> | undefined = undefined;
  services$: Observable<PlatformService[]> | undefined = undefined;
  existingObjects: RoleObject[] | undefined = undefined;
  existingServices: ServiceRole[] | undefined = undefined;

  nameControlErrors = [INPUT_TEXT_REQUIRED_ERROR, NAME_PATTERN_ERROR];
  descriptionControlErrors = [INPUT_TEXT_REQUIRED_ERROR, DESCRIPTION_PATTERN_ERROR];

  objectColumns = [{ name: 'Object Name', prop: 'name', flexGrow: 1 }];
  serviceColumns = [{ name: 'Service Name', prop: 'name', flexGrow: 1 }];
  constructor(
    private activatedRoute: ActivatedRoute,
    private roleService: RolesService,
    private alertService: AlertService,
    private store: Store<any>,
    private formBuilder: FormBuilder,
    private roleDetailsFacade: RoleDetailsFacadeService
  ) {
    this.verifyRouteId();
    this.roleDetailsFacade.initialize(this._roleId);
    this.role$ = this.roleDetailsFacade.role$;
    this.loadForm();
  }

  verifyRouteId() {
    const roleId = readParameterFromRouterState(this.activatedRoute, 'id');
    if (!roleId) {
      throw new Error('role id isnt in route');
    }
    this._roleId = roleId;
  }

  loadForm() {
    this.roleForm = this.buildRoleForm();
    this.roleNameControl = this.roleForm.get('roleName') as FormControl;
    this.roleTitleControl = this.roleForm.get('roleTitle') as FormControl;
    this.roleDescriptionControl = this.roleForm.get('roleDescription') as FormControl;

    this.role$.pipe(takeUntil(this.destroy$)).subscribe((role: any) => {
      this.roleNameControl.setValue(role.name);
      this.roleTitleControl.setValue(role.title);
      this.roleDescriptionControl.setValue(role.description);
      const objs: ScopeCategory[] =
        role &&
        role.role_objects.map((role_obj: RoleObject) => {
          return { id: role_obj.object_id, name: role_obj.object_name };
        });
      this.objects$ = of(objs);
      if (objs.length > 0) {
        this.existingObjects = role.role_objects;
      }
      const svcRole: PlatformService[] =
        role &&
        role.service_role.map((svc_rol: ServiceRole) => {
          return { id: svc_rol.service_id, name: svc_rol.service_name };
        });
      this.services$ = of(svcRole);
      if (objs.length > 0) {
        this.existingServices = role.service_role;
      }
    });
  }

  submit() {
    if (!this._roleId) {
      return;
    }

    if (!this.roleForm.valid) {
      return;
    }

    const request = {
      roleName: this.roleNameControl.value,
      roleTitle: this.roleTitleControl.value,
      roleDescription: this.roleDescriptionControl.value,
      role_objects: this.getRoleObjects(),
      service_role: this.getServiceRole()
    };

    this.roleService
      .updateRole(this._roleId, request)
      .pipe(take(1))
      .subscribe(
        () => {
          this.store.dispatch(new GoAction(['/iam/roles']));
          this.store.dispatch(new LoadingRoleDetailsAction(this._roleId));
          this.alertService.success('Role has successfully been updated.');
        },
        () => {
          this.alertService.error('There was an error updating the role.');
        }
      );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  private buildRoleForm() {
    return this.formBuilder.group({
      roleName: ['', [Validators.required, Validators.pattern(NAME_PATTERN)]],
      roleTitle: ['', [Validators.required, Validators.pattern(DESCRIPTION_PATTERN)]],
      roleDescription: ['', [Validators.required, Validators.pattern(DESCRIPTION_PATTERN)]]
    });
  }

  private getRoleObjects() {
    return this.selectedObjects !== undefined
      ? this.selectedObjects.map(roleObject => {
          return {
            object_id: roleObject.id,
            object_role_id: '',
            object_name: roleObject.name,
            role_id: this._roleId
          };
        })
      : this.existingObjects;
  }

  private getServiceRole() {
    return this.selectedServices !== undefined
      ? this.selectedServices.map(svcRole => {
          return {
            service_id: svcRole.id,
            service_role_id: '',
            service_name: svcRole.name,
            role_id: this._roleId
          };
        })
      : this.existingServices;
  }

  onSelectObjectRows(rows: SelectedRows) {
    this.selectedObjects = rows.selected;
  }

  onSelectServiceRows(rows: SelectedRows) {
    this.selectedServices = rows.selected;
  }

  isFormValid() {
    return this.roleForm.valid;
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/roles/role-edit/role-edit.component.ts